import React, { useMemo } from "react";
import {
    Alert,
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import Container from "../../components/Container";
import Main from "../../layouts/Main";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import UserTable from "../../components/agentComponents/UserTable";
import { User } from "../../services/restserver-openapi";
import { setTabNumber } from "../../store/Agent/userOverviewSlice";

import HomeWorkRoundedIcon from "@mui/icons-material/HomeWorkRounded";
import DirectionsCarRoundedIcon from "@mui/icons-material/DirectionsCarRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";

type FilterItem = {
    key: number; // entspricht tabNumber
    title: string;
    subtitle: string;
    icon: React.ReactNode;
    role: User.userRole;
};

const AgentUserOverviewPage = () => {
    const theme = useTheme();
    const mode = theme.palette.mode;

    const dispatch = useAppDispatch();
    const tabNumber = useAppSelector((state) => state.userOverview.tabNumber);
    const isVerifiedAgent = useAppSelector((state) => state.user.isVerifiedAgent);

    const filters: FilterItem[] = useMemo(
        () => [
            {
                key: 0,
                title: "Hosts",
                subtitle: "Gastgeber verwalten",
                icon: <HomeWorkRoundedIcon />,
                role: User.userRole.HOST,
            },
            {
                key: 1,
                title: "Vanlifer",
                subtitle: "Kunden verwalten",
                icon: <DirectionsCarRoundedIcon />,
                role: User.userRole.VANLIFER,
            },
            {
                key: 2,
                title: "Agents",
                subtitle: "Agenten verwalten",
                icon: <SupportAgentRoundedIcon />,
                role: User.userRole.AGENT,
            },
        ],
        []
    );

    const activeFilter = filters.find((f) => f.key === tabNumber) ?? filters[0];

    if (!isVerifiedAgent) {
        return (
            <Main>
                <Container>
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            p: 2,
                            border: 0,
                            boxShadow: "none",
                            bgcolor: "background.paper",
                        }}
                    >
                        <Alert severity="error">
                            Sie haben keine Agent-Berechtigung. Womöglich ist Ihr Account noch nicht freigeschaltet.
                        </Alert>
                    </Paper>
                </Container>
            </Main>
        );
    }

    return (
        <Main>
            <Container>
                {/* Header im BookingOverviewPage-Style */}
                <Typography
                    sx={{
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: 1,
                    }}
                    gutterBottom
                    color="text.secondary"
                >
                    Administration
                </Typography>

                <Box mb={2}>
                    <Typography variant="h3" component="h2" color="text.primary" fontWeight={700} lineHeight={1.2}>
                        User{" "}
                        <Typography
                            component="span"
                            variant="inherit"
                            color="text.primary"
                            sx={{
                                background: `linear-gradient(180deg, transparent 82%, ${alpha(
                                    mode === "light" ? theme.palette.secondary.main : theme.palette.primary.main,
                                    0.3
                                )} 0%)`,
                            }}
                        >
                            Management
                        </Typography>
                    </Typography>

                    <Box mb={3}>
                        <Typography variant="h6" color="text.secondary">
                            Wähle links eine Kategorie aus, um die jeweiligen Nutzer zu sehen und zu verwalten.
                        </Typography>
                    </Box>
                </Box>

                {/* Layout wie BookingOverview: Sidebar + Content */}
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "320px 1fr" }, gap: 2 }}>
                    {/* LEFT: Sidebar Filter */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            p: 2,
                            height: "fit-content",
                            border: 0,
                            boxShadow: "none",
                            bgcolor: "background.paper",
                        }}
                    >
                        <List sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                            {filters.map((f) => {
                                const active = tabNumber === f.key;

                                return (
                                    <ListItem key={f.key} disablePadding>
                                        <ListItemButton
                                            onClick={() => dispatch(setTabNumber(f.key))}
                                            selected={active}
                                            sx={{
                                                borderRadius: 2,
                                                px: 1.25,
                                                py: 1,
                                                gap: 0.5,
                                                "&.Mui-selected": {
                                                    bgcolor: "action.selected",
                                                    "&:hover": { bgcolor: "action.selected" },
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 40, color: active ? "primary.main" : "text.secondary" }}>
                                                {f.icon}
                                            </ListItemIcon>

                                            <ListItemText
                                                primary={<Typography sx={{ fontWeight: 800 }}>{f.title}</Typography>}
                                                secondary={f.subtitle}
                                                secondaryTypographyProps={{ color: "text.secondary" }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Paper>

                    {/* RIGHT: Content */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            p: 2,
                            border: 0,
                            boxShadow: "none",
                            bgcolor: "background.paper",
                        }}
                    >
                        <UserTable key={activeFilter.role} userRole={activeFilter.role} />
                    </Paper>
                </Box>
            </Container>
        </Main>
    );
};

export default AgentUserOverviewPage;
